import { Controller } from "@hotwired/stimulus"
import { loadTranslations, t } from "../shared/utils"

/**
 * Age category autocomplete with checkboxes.
 * - Type to filter (e.g. "senior" shows Senior group with Pre-Senior, Senior 1, etc.)
 * - Click group header to select/deselect all in that group
 * - Click individual checkbox to toggle
 * - Data: age-category-autocomplete-data-value = JSON array of {id, name, group}
 */
export default class extends Controller {
  static targets = ["input", "results", "container"]
  static values = {
    data: Array,
    selectedIds: Array,
    placeholder: String,
    changeEvent: { type: String, default: "change" }
  }

  connect() {
    loadTranslations()
    this._categories = this.dataValue || []
    this._selectedIds = new Set((this.selectedIdsValue || []).map(String))
    this._renderCheckboxes()
    this._syncInputDisplay()
    this._syncHiddenInputs()
    document.addEventListener("click", this._handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this._handleClickOutside)
  }

  search() {
    this._renderCheckboxes()
  }

  toggleCategory(event) {
    event.preventDefault()
    event.stopPropagation()
    const label = event.currentTarget
    const id = String(label.dataset.id)
    if (!id) return
    if (this._selectedIds.has(id)) {
      this._selectedIds.delete(id)
    } else {
      this._selectedIds.add(id)
    }
    this._syncHiddenInputs()
    this._syncInputDisplay()
    this._renderCheckboxes()
    this._dispatchChange()
  }

  toggleGroup(event) {
    event.preventDefault()
    event.stopPropagation()
    const group = event.currentTarget.dataset.group
    if (!group) return
    const groupCats = this._categories.filter((c) => c.group === group)
    const allSelected = groupCats.every((c) => this._selectedIds.has(String(c.id)))
    if (allSelected) {
      groupCats.forEach((c) => this._selectedIds.delete(String(c.id)))
    } else {
      groupCats.forEach((c) => this._selectedIds.add(String(c.id)))
    }
    this._syncHiddenInputs()
    this._syncInputDisplay()
    this._renderCheckboxes()
    this._dispatchChange()
  }

  openResults() {
    this.resultsTarget.classList.remove("d-none")
    this._renderCheckboxes()
  }

  closeResults() {
    this.resultsTarget.classList.add("d-none")
  }

  _handleClickOutside = (event) => {
    if (!this.element.contains(event.target)) {
      this.closeResults()
    }
  }

  _getSearchQuery() {
    const raw = (this.inputTarget?.value || "").trim()
    if (!raw) return ""
    // Gdy pole pokazuje liczbę zaznaczonych (np. "6 kategorii"), nie traktuj tego jako wyszukiwania
    const count = this._selectedIds.size
    if (count > 0) {
      const word =
        count === 1
          ? (t("profiles.index.age_category_count_one") || "kategoria")
          : count >= 2 && count <= 4
            ? (t("profiles.index.age_category_count_few") || "kategorie")
            : (t("profiles.index.age_category_count_many") || "kategorii")
      if (raw === `${count} ${word}`) return ""
    }
    return raw.toLowerCase()
  }

  _renderCheckboxes() {
    const query = this._getSearchQuery()
    const filtered = this._filterByQuery(query)

    // Group by group
    const byGroup = {}
    filtered.forEach((c) => {
      if (!byGroup[c.group]) byGroup[c.group] = []
      byGroup[c.group].push(c)
    })

    const groupOrder = ["youth", "adult", "senior"]
    const sortedGroups = Object.keys(byGroup).sort(
      (a, b) => groupOrder.indexOf(a) - groupOrder.indexOf(b) || a.localeCompare(b)
    )

    this.resultsTarget.innerHTML = sortedGroups
      .map((group) => {
        const cats = byGroup[group]
        const groupLabel = this._groupLabel(group)
        const allSelected = cats.every((c) => this._selectedIds.has(String(c.id)))
        const someSelected = cats.some((c) => this._selectedIds.has(String(c.id)))
        const groupCheckClass = allSelected ? "form-check-input-checked" : someSelected ? "form-check-input-indeterminate" : ""
        return `
          <div class="age-category-group">
            <div class="age-category-group-header" data-action="click->age-category-autocomplete#toggleGroup" data-group="${group}">
              <input type="checkbox" class="form-check-input ${groupCheckClass}" ${allSelected ? "checked" : ""} tabindex="-1" readonly>
              <span class="fw-semibold">${escapeHtml(groupLabel)}</span>
            </div>
            <div class="age-category-group-items">
              ${cats
                .map(
                  (c) => `
                <label class="age-category-item" data-id="${c.id}" data-action="click->age-category-autocomplete#toggleCategory">
                  <input type="checkbox" class="form-check-input" tabindex="-1" readonly ${this._selectedIds.has(String(c.id)) ? "checked" : ""}>
                  <span>${escapeHtml(c.name)}</span>
                </label>
              `
                )
                .join("")}
            </div>
          </div>
        `
      })
      .join("")

    if (sortedGroups.length === 0) {
      const noResults = t("profiles.index.age_category_no_results") || "Brak wyników"
      this.resultsTarget.innerHTML = `<div class="p-2 text-muted small">${escapeHtml(noResults)}</div>`
    }
  }

  _filterByQuery(query) {
    if (!query) return this._categories
    const q = query.toLowerCase()
    return this._categories.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.group || "").toLowerCase().includes(q) ||
        (this._groupLabel(c.group) || "").toLowerCase().includes(q)
    )
  }

  _groupLabel(group) {
    const key = `profiles.index.age_category_group_${group}`
    const label = t(key)
    return label !== key ? label : (group ? group.charAt(0).toUpperCase() + group.slice(1) : "")
  }

  _syncHiddenInputs() {
    const container = this.element.querySelector(".age-category-ids-container")
    if (!container) return
    container.innerHTML = ""
    this._selectedIds.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "age_category_ids[]"
      input.value = id
      container.appendChild(input)
    })
  }

  _syncInputDisplay() {
    if (!this.inputTarget) return
    const count = this._selectedIds.size
    const placeholder = this.placeholderValue || t("profiles.index.age_category_placeholder") || "Wpisz kategorię, np. senior..."
    if (count === 0) {
      this.inputTarget.placeholder = placeholder
      this.inputTarget.value = ""
    } else {
      this.inputTarget.placeholder = ""
      const word =
        count === 1
          ? (t("profiles.index.age_category_count_one") || "kategoria")
          : count >= 2 && count <= 4
            ? (t("profiles.index.age_category_count_few") || "kategorie")
            : (t("profiles.index.age_category_count_many") || "kategorii")
      this.inputTarget.value = `${count} ${word}`
    }
  }

  _dispatchChange() {
    this.element.dispatchEvent(new Event(this.changeEventValue, { bubbles: true }))
  }
}

function escapeHtml(str) {
  if (!str) return ""
  const div = document.createElement("div")
  div.textContent = str
  return div.innerHTML
}
